import { useSearchParams } from "react-router-dom";

import {
  Box,
  Collapse,
  IconButton,
  List,
  ListItemButton,
  ListItemIcon,
  ListItemText,
  Stack,
  Typography,
  styled,
  useTheme,
} from "@mui/material";
import { useState } from "react";
import { hasPermission } from "../../../components";
import Permissions from "../../../constants/permissions";
import useAuth from "../../../context/UserContext";

import { CaretDownIcon } from "@phosphor-icons/react/dist/csr/CaretDown";
import { CaretRightIcon } from "@phosphor-icons/react/dist/csr/CaretRight";
import { XCircleIcon } from "@phosphor-icons/react/dist/csr/XCircle";
import { useEditorStoreContext } from "../../../store/editor/EditorStoreProvider";
import { EditorTabVariant } from "../../../store/editor/editorStore";
import { File } from "../../../store/editor/types";

const ListItemButtonFile = styled(ListItemButton)({
  paddingTop: 0,
  paddingBottom: 0,
});

const ListItemButtonErrorMsg = styled(ListItemButton)(({ theme }) => ({
  paddingTop: 0,
  paddingBottom: 0,
  paddingLeft: theme.spacing(4),
}));

const ListItemIconError = styled(ListItemIcon)({
  minWidth: "22px",
});

const ProblemsFromFile = ({ file }: { file: File }) => {
  const theme = useTheme();
  const [open, setOpen] = useState(true);
  const [, setSearchParams] = useSearchParams();

  const { user } = useAuth();
  const messages = useEditorStoreContext((state) =>
    state.compilationResult[file.path]?.messages?.filter(
      (msg) => !(msg.type === "beta") || hasPermission(Permissions.BetaMessages, user)
    )
  );
  const openFileTab = useEditorStoreContext((state) => state.openFileTab);

  if (!messages || messages.length == 0) {
    return null;
  }
  const handleClickProblem = (line: number) => {
    openFileTab(file.path, "file", file.editFlags?.originalPath, EditorTabVariant.Editor);
    setSearchParams({
      mode: EditorTabVariant.Editor,
      file: encodeURIComponent(file.path),
      lines: `${line}-${line}-1-1`,
    });
  };
  return (
    <>
      <ListItemButtonFile onClick={() => setOpen(!open)}>
        {open ? <CaretDownIcon /> : <CaretRightIcon />}
        <ListItemText primary={file.path} />
      </ListItemButtonFile>
      <Collapse in={open} timeout="auto" unmountOnExit>
        <List component="div" disablePadding data-testid={`error-msgs-list-${file.path}`}>
          {messages?.map((message, index) => (
            <ListItemButtonErrorMsg key={index} onClick={() => handleClickProblem(message.line)}>
              <ListItemIconError>
                <XCircleIcon color={message.type == "error" ? theme.palette.error.main : theme.palette.warning.main} />
              </ListItemIconError>
              <ListItemText primary={message.message} />
            </ListItemButtonErrorMsg>
          ))}
        </List>
      </Collapse>
    </>
  );
};
const ProblemsSection = ({
  showProblems,
  setShowProblems,
}: {
  showProblems: boolean;
  setShowProblems: (show: boolean) => void;
}) => {
  const files = useEditorStoreContext((state) => state.files);
  return (
    <Box sx={{ padding: 1.5, borderTop: "0.5px solid", height: "100%" }}>
      <Stack direction="row" sx={{ justifyContent: "space-between", alignItems: "center" }}>
        <Typography variant="heading.six">Problems</Typography>
        <IconButton onClick={() => setShowProblems(!showProblems)}>
          {showProblems ? <CaretDownIcon /> : <CaretRightIcon />}
        </IconButton>
      </Stack>
      {showProblems ? (
        <List disablePadding sx={{ maxHeight: "90%", overflow: "auto" }} component="div">
          {files.map((file) => (
            <ProblemsFromFile key={file.id} file={file} />
          ))}
        </List>
      ) : null}
    </Box>
  );
};

export default ProblemsSection;
